import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth";
import type { Capability } from "./types/api";
import { Spinner } from "./components/ui";
import AppShell from "./components/shell/AppShell";
import Login from "./pages/Login";
import Monitor from "./features/monitor/Monitor";
import Analyze from "./features/analyze/Analyze";
import Comply from "./features/comply/Comply";
import Configure from "./features/configure/Configure";
import Developer from "./features/developer/Developer";
import Triage from "./features/triage/Triage";
import Geospatial from "./features/geospatial/Geospatial";
import ExportHistory from "./features/exports/ExportHistory";
import Cases from "./features/cases/Cases";
import Admin from "./features/admin/Admin";

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner label="Authenticating…" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Landing() {
  const { user } = useAuth();
  const surface = user?.landing.surface ?? "monitor";
  return <Navigate to={`/${surface}`} replace />;
}

/** Gate a surface on holding at least one of the given capabilities (active tenant). */
function RequireCapability({ anyOf, children }: { anyOf: Capability[]; children: JSX.Element }) {
  const { capabilities } = useAuth();
  if (!anyOf.some((c) => capabilities.includes(c))) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Landing />} />
        <Route path="monitor" element={<Monitor />} />
        <Route path="cases" element={<Cases />} />
        <Route path="map" element={<Geospatial />} />
        <Route path="triage" element={<Triage />} />
        <Route path="analyze" element={<Analyze />} />
        <Route path="comply" element={<RequireCapability anyOf={["platform.manage"]}><Comply /></RequireCapability>} />
        <Route path="configure" element={<RequireCapability anyOf={["config.write"]}><Configure /></RequireCapability>} />
        <Route path="developer" element={<RequireCapability anyOf={["config.write", "apikey.manage"]}><Developer /></RequireCapability>} />
        <Route path="exports" element={<ExportHistory />} />
        <Route path="admin" element={<RequireCapability anyOf={["platform.manage", "config.write", "apikey.manage"]}><Admin /></RequireCapability>} />
        <Route path="admin/:resource" element={<RequireCapability anyOf={["platform.manage", "config.write", "apikey.manage"]}><Admin /></RequireCapability>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
